import React, { useState, useRef, useEffect } from 'react'
import { styles } from './index.sass'
import { Button, message, Card, Modal, Spin as AntdSpin, Input } from 'antd'
import RichTextEditor from '../../components/richTextEditor'
import { sendEmail, sendTestEmail } from '../../api/mailchimp'
import { cleanHtmlContent } from '../../utils/htmlCleaner'

export const PowerList = () => {
  const [contentHtml, setContentHtml] = useState('')
  const [isEditing, setIsEditing] = useState(false)
  const [loading, setLoading] = useState(false)
  const [editedContent, setEditedContent] = useState('')
  const [progress, setProgress] = useState([])
  const [inputPayload, setInputPayload] = useState('')
  const editorContentRef = useRef('')
  const eventSourceRef = useRef(null)

  const handleGenerate = async (regenerate = false) => {
    setLoading(true)
    setProgress([])
    
    try {
      // Connect to SSE endpoint with payload as query parameter (since SSE doesn't support request bodies)
      const params = new URLSearchParams({
        regenerate: true,
        topic: inputPayload
      })
      const url = `${process.env.REACT_APP_ML_API_ENDPOINT}/api/ml/generate-powerlist-stream?${params.toString()}`
      const eventSource = new EventSource(url)
      eventSourceRef.current = eventSource

      eventSource.onmessage = (event) => {
        // SSE data comes with "data: " prefix - we need to parse the actual JSON
        const data = JSON.parse(event.data)
        
        if (data.step !== undefined) {
          // Update progress
          setProgress(prev => [...prev, data])
        } else if (data.status === 'success') {
          // Power list generation completed
          const cleanedHtml = cleanHtmlContent(data.content_html)
          setContentHtml(cleanedHtml)
          message.success('Power List generated successfully!')
          eventSource.close()
          setLoading(false)
        } else if (data.status === 'error') {
          // Error occurred
          console.error('Error generating Power List:', data.message)
          message.error(data.message || 'Failed to generate Power List. Please try again.')
          eventSource.close()
          setLoading(false)
        }
      }

      eventSource.onerror = () => {
        console.error('Stream connection error')
        eventSource.close()
        setLoading(false)
      }

    } catch (err) {
      console.error('Error generating Power List:', err)
      message.error(
        err.message || 'Failed to generate Power List. Please try again.',
      )
      setLoading(false)
    }
  }

  const handleEdit = () => {
    setEditedContent(contentHtml)
    editorContentRef.current = contentHtml
    setIsEditing(true)
  }

  const handleSave = () => {
    const finalContent = editorContentRef.current || editedContent
    setContentHtml(finalContent)
    setIsEditing(false)
    message.success('Changes saved successfully!')
  }

  const handleCancel = () => {
    setEditedContent(contentHtml)
    editorContentRef.current = ''
    setIsEditing(false)
    message.info('Changes discarded')
  }

  const handleEditorChange = (html) => {
    editorContentRef.current = html
  }

  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
      }
    }
  }, [])

  const handleCopy = async () => {
    try {
      const cleanedHtml = cleanHtmlContent(contentHtml)

      if (typeof window !== 'undefined' && window.ClipboardItem) {
        const clipboardItem = new window.ClipboardItem({
          'text/html': new Blob([cleanedHtml], { type: 'text/html' }),
          'text/plain': new Blob([cleanedHtml], { type: 'text/plain' })
        })

        await navigator.clipboard.write([clipboardItem])
      } else {
        navigator.clipboard.writeText(cleanedHtml)
      }
      message.success('Copied to clipboard')
    } catch (err) {
      const cleanedHtml = cleanHtmlContent(contentHtml)
      navigator.clipboard.writeText(cleanedHtml)
      message.success('Copied to clipboard')
    }
  }

  const handleRegenerate = () => {
    Modal.confirm({
      title: 'Regenerate Power List',
      content: 'This will replace the current content. Continue?',
      onOk: () => handleGenerate(true)
    })
  }

  const handleSendEmail = () => {
    Modal.confirm({
      title: 'Send Email',
      content: 'Are you sure you want to send this email?',
      okText: 'Send',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const subject = 'Power List'
          const response = await sendEmail({ content: contentHtml, subject: subject })
          if (response.success === true) {
            message.success('Email sent successfully!')
          } else {
            message.error('Failed to send email. Please try again.')
          }
        } catch (err) {
          console.error('Error sending email:', err)
          message.error('Failed to send email. Please try again.')
        }
      }
    })
  }

  const handleSendTestEmail = async () => {
    try {
      const subject = 'Test Power List Email'
      const response = await sendTestEmail({ html: contentHtml, subject: subject })
      if (response.success === true) {
        message.success('Test email sent successfully!')
      } else {
        message.error('Failed to send test email. Please try again.')
      }
    } catch (err) {
      console.error('Error sending test email:', err)
      message.error('Failed to send test email. Please try again.')
    }
  }

  return (
    <div className={styles}>
      {loading && (
        <div className="loading-overlay">
          <div className="loading-popup">
            <AntdSpin size="large" style={{ marginBottom: '16px' }} />
            <div className="progress-steps">
              {progress.map((step, index) => (
                <div key={index} className={`progress-step step-${step.step} ${step.status}`}>
                  {step.message}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      <div className="container">
        <h2>Power List Content Generator</h2>
        <div className="input-section">
          <Input
            placeholder="Optional: Enter a specific topic or focus for the Power List"
            value={inputPayload}
            onChange={(e) => setInputPayload(e.target.value)}
            style={{ marginBottom: '16px', width: '100%' }}
          />
        </div>
        <div className="actions">
          <Button type="primary" onClick={handleGenerate} loading={loading}>
            Generate Power List
          </Button>
          <Button onClick={handleRegenerate} disabled={!contentHtml || loading}>
            Regenerate
          </Button>
          <Button onClick={handleCopy} disabled={!contentHtml}>
            Copy Content
          </Button>
          <Button onClick={handleEdit} disabled={!contentHtml}>
            Edit Content
          </Button>
          <Button onClick={handleSendTestEmail} disabled={!contentHtml}>
            Send test Email
          </Button>
          <Button onClick={handleSendEmail} disabled={!contentHtml}>
            Send Email
          </Button>
        </div>
        {contentHtml && (
          <Card className="content-card">
            {isEditing ? (
              <div>
                <div style={{ marginBottom: '16px', textAlign: 'right' }}>
                  <Button onClick={handleCancel} style={{ marginRight: '8px' }}>
                    Cancel
                  </Button>
                  <Button type="primary" onClick={handleSave}>
                    Save Changes
                  </Button>
                </div>
                <RichTextEditor
                  value={editedContent}
                  onChange={handleEditorChange}
                  placeholder="Edit your Power List content here..."
                />
              </div>
            ) : (
              <>
                <div
                  className="content-preview"
                  dangerouslySetInnerHTML={{ __html: contentHtml }}
                />
              </>
            )}
          </Card>
        )}
      </div>
    </div>
  )
}
